import { useCallback, useEffect, useRef, useState } from 'react'
import type { UserNotification, UserNotificationPage } from '../api/contracts'
import {
  getNotificationsPage,
  markAllNotificationsRead,
  markNotificationRead
} from '../api/notifications/feed'
import type { NotificationsPageParams } from '../api/notifications/feed'
import type { NotificationsFilter } from '../components/notifications/notificationsText'
import { isAbortError } from '../utils/errorUtils'

export type NotificationsFeedStatus = 'idle' | 'loading' | 'error' | 'ready'
export type MarkNotificationReadOutcome = 'read' | 'missing' | 'failed'

export interface NotificationsFeedState {
  filter: NotificationsFilter
  setFilter: (filter: NotificationsFilter) => void
  items: UserNotification[]
  status: NotificationsFeedStatus
  errorMessage: string | null
  isLoadingMore: boolean
  hasMore: boolean
  /** `totalCount` of the last loaded first page without filters, for the «Все N» chip. */
  allCount: number | null
  actionErrorMessage: string | null
  isMarkingAll: boolean
  loadMore: () => void
  retry: () => void
  refreshFirstPage: () => void
  markRead: (notificationId: number) => Promise<MarkNotificationReadOutcome>
  markAllRead: () => Promise<boolean>
}

const FILTER_QUERIES: Record<NotificationsFilter, Omit<NotificationsPageParams, 'page'>> = {
  all: { unread: 'any', kind: 'all' },
  unread: { unread: 'only', kind: 'all' },
  comments: { unread: 'any', kind: 'comments' },
  payments: { unread: 'any', kind: 'payments' },
  statuses: { unread: 'any', kind: 'statuses' }
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
  return error instanceof Error && error.message ? error.message : fallbackMessage
}

function appendWithoutDuplicates(current: UserNotification[], next: UserNotification[]): UserNotification[] {
  const knownIds = new Set(current.map((item) => item.id))
  return [...current, ...next.filter((item) => !knownIds.has(item.id))]
}

function prependNew(current: UserNotification[], firstPage: UserNotification[]): UserNotification[] {
  const knownIds = new Set(current.map((item) => item.id))
  return [...firstPage.filter((item) => !knownIds.has(item.id)), ...current]
}

function setReadAt(items: UserNotification[], notificationId: number, readAtUtc: string | null): UserNotification[] {
  return items.map((item) => (item.id === notificationId ? { ...item, readAtUtc } : item))
}

export function useNotificationsFeed(apiBaseUrl: string, { active }: { active: boolean }): NotificationsFeedState {
  const [filter, setFilterState] = useState<NotificationsFilter>('all')
  const [items, setItems] = useState<UserNotification[]>([])
  const [status, setStatus] = useState<NotificationsFeedStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [allCount, setAllCount] = useState<number | null>(null)
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null)
  const [isMarkingAll, setIsMarkingAll] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const extraRequestRef = useRef<AbortController | null>(null)

  const applyTotals = useCallback((result: UserNotificationPage, forFilter: NotificationsFilter) => {
    setTotalPages(result.totalPages)
    if (forFilter === 'all') {
      setAllCount(result.totalCount)
    }
  }, [])

  // First page: on activation, on a filter change and on retry. Each change makes exactly one request.
  useEffect(() => {
    if (!active) {
      setStatus('idle')
      return
    }

    const controller = new AbortController()
    extraRequestRef.current?.abort()
    setStatus('loading')
    setErrorMessage(null)
    setActionErrorMessage(null)
    setItems([])
    setPage(1)
    setTotalPages(0)
    setIsLoadingMore(false)

    getNotificationsPage(apiBaseUrl, { page: 1, ...FILTER_QUERIES[filter] }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        setItems(result.items)
        applyTotals(result, filter)
        setStatus('ready')
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return
        setErrorMessage(getErrorMessage(error, 'Не удалось загрузить уведомления.'))
        setStatus('error')
      })

    return () => {
      controller.abort()
    }
  }, [active, apiBaseUrl, filter, reloadKey, applyTotals])

  useEffect(() => () => {
    extraRequestRef.current?.abort()
  }, [])

  const startExtraRequest = useCallback((): AbortController => {
    extraRequestRef.current?.abort()
    const controller = new AbortController()
    extraRequestRef.current = controller
    return controller
  }, [])

  const setFilter = useCallback((nextFilter: NotificationsFilter) => {
    setFilterState((current) => (current === nextFilter ? current : nextFilter))
  }, [])

  const retry = useCallback(() => {
    setReloadKey((key) => key + 1)
  }, [])

  const loadMore = useCallback(() => {
    if (status !== 'ready' || isLoadingMore || page >= totalPages) {
      return
    }
    const nextPage = page + 1
    const requestFilter = filter
    const controller = startExtraRequest()
    setIsLoadingMore(true)
    setActionErrorMessage(null)

    getNotificationsPage(apiBaseUrl, { page: nextPage, ...FILTER_QUERIES[requestFilter] }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        setItems((current) => appendWithoutDuplicates(current, result.items))
        setPage(nextPage)
        applyTotals(result, requestFilter)
        setIsLoadingMore(false)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return
        setActionErrorMessage(getErrorMessage(error, 'Не удалось загрузить уведомления.'))
        setIsLoadingMore(false)
      })
  }, [apiBaseUrl, applyTotals, filter, isLoadingMore, page, startExtraRequest, status, totalPages])

  const refreshFirstPage = useCallback(() => {
    if (!active || status !== 'ready' || isLoadingMore) {
      return
    }
    const requestFilter = filter
    const controller = startExtraRequest()

    getNotificationsPage(apiBaseUrl, { page: 1, ...FILTER_QUERIES[requestFilter] }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        setItems((current) => prependNew(current, result.items))
        applyTotals(result, requestFilter)
      })
      .catch(() => {
        // A background refresh keeps the rows already shown; the next signal or opening reloads them.
      })
  }, [active, apiBaseUrl, applyTotals, filter, isLoadingMore, startExtraRequest, status])

  const markRead = useCallback(async (notificationId: number): Promise<MarkNotificationReadOutcome> => {
    setItems((current) => setReadAt(current, notificationId, new Date().toISOString()))
    try {
      const updated = await markNotificationRead(apiBaseUrl, notificationId)
      if (updated === null) {
        setItems((current) => current.filter((item) => item.id !== notificationId))
        return 'missing'
      }
      setItems((current) => setReadAt(current, notificationId, updated.readAtUtc))
      return 'read'
    } catch {
      setItems((current) => setReadAt(current, notificationId, null))
      return 'failed'
    }
  }, [apiBaseUrl])

  const markAllRead = useCallback(async (): Promise<boolean> => {
    setIsMarkingAll(true)
    setActionErrorMessage(null)
    try {
      await markAllNotificationsRead(apiBaseUrl)
      const readAtUtc = new Date().toISOString()
      setItems((current) => current.map((item) => (item.readAtUtc === null ? { ...item, readAtUtc } : item)))
      return true
    } catch (error) {
      setActionErrorMessage(getErrorMessage(error, 'Не удалось отметить уведомления прочитанными.'))
      return false
    } finally {
      setIsMarkingAll(false)
    }
  }, [apiBaseUrl])

  return {
    filter,
    setFilter,
    items,
    status,
    errorMessage,
    isLoadingMore,
    hasMore: status === 'ready' && page < totalPages,
    allCount,
    actionErrorMessage,
    isMarkingAll,
    loadMore,
    retry,
    refreshFirstPage,
    markRead,
    markAllRead
  }
}
